import React, { useEffect, useState } from 'react';
import { ArrowRight, Code2, Rocket } from 'lucide-react';
import { Button } from './ui/button';

const HeroSection = () => {
  const [currentRoleIndex, setCurrentRoleIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isTyping, setIsTyping] = useState(true);
  
  const roles = ['Full Stack Developer', 'Cloud Architect', 'AI Engineer', 'DevOps Expert'];
  
  useEffect(() => {
    const currentRole = roles[currentRoleIndex];
    let currentIndex = 0;
    
    const typeText = () => {
      if (currentIndex <= currentRole.length) {
        setDisplayText(currentRole.slice(0, currentIndex));
        currentIndex++;
        setTimeout(typeText, 100);
      } else {
        setIsTyping(false);
        setTimeout(() => {
          // Wait a bit before starting to delete
          deleteText();
        }, 2000);
      }
    };
    
    const deleteText = () => {
      if (currentIndex > 0) {
        setDisplayText(currentRole.slice(0, currentIndex - 1));
        currentIndex--;
        setTimeout(deleteText, 50);
      } else {
        setIsTyping(true);
        setCurrentRoleIndex((prev) => (prev + 1) % roles.length);
      }
    };
    
    if (isTyping) {
      typeText();
    }
  }, [currentRoleIndex, isTyping]);

  const scrollToSection = (sectionId) => {
    document.querySelector(sectionId)?.scrollIntoView({ behavior: 'smooth' });
  };

  const stats = [
    { value: '7+', label: 'Years Experience', color: 'from-blue-400 to-cyan-400' },
    { value: '20+', label: 'Projects Delivered', color: 'from-purple-400 to-pink-400' },
    { value: '5+', label: 'Certifications', color: 'from-green-400 to-emerald-400' },
    { value: '15+', label: 'Engineers Mentored', color: 'from-orange-400 to-red-400' },
  ];

  const skills = [
    { name: 'Cloud Native', icon: '☁️' },
    { name: 'AI/ML', icon: '🤖' },
    { name: 'Full Stack', icon: '⚡' },
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center pt-20">
      <div className="container mx-auto px-6 z-10">
        <div className="text-center space-y-8">
          {/* Status Badge - removed "Available for Projects" */}

          {/* Main Heading */}
          <div className="space-y-6">
            <h1 className="text-6xl md:text-8xl font-bold text-white leading-tight">
              Sony Joy
            </h1>
            <div className="h-16 flex items-center justify-center">
              <span className="text-2xl md:text-4xl font-medium text-gray-300">
                I'm a{' '}
                <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                  {displayText}
                  <span className="animate-pulse text-blue-400">|</span>
                </span>
              </span>
            </div>
          </div>

          {/* Description */}
          <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
            I craft end-to-end solutions with{' '}
            <span className="text-blue-400 font-semibold">7 years of experience</span>{' '}
            building scalable applications, cloud infrastructure, and AI-powered systems that drive business transformation.
          </p>

          {/* Skills Tags */}
          <div className="flex flex-wrap justify-center gap-4">
            {skills.map((skill, index) => (
              <div
                key={index}
                className="flex items-center px-4 py-2 rounded-full bg-gray-800/50 border border-gray-700/50 text-gray-300 backdrop-blur-sm"
              >
                <span className="mr-2">{skill.icon}</span>
                <span className="font-medium">{skill.name}</span>
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button 
              onClick={() => scrollToSection('#projects')}
              className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white border-0 rounded-full px-8 py-6 text-lg font-semibold transition-all duration-200 hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <Rocket className="w-5 h-5 mr-2" />
              View My Work
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            
            <Button 
              onClick={() => scrollToSection('#contact')}
              className="bg-transparent border-2 border-gray-600 hover:border-white text-white rounded-full px-8 py-6 text-lg font-medium transition-all duration-200 hover:bg-white hover:text-gray-900"
            >
              <Code2 className="w-5 h-5 mr-2" />
              Let's Connect
            </Button>
          </div>
        </div>

        {/* Stats Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-20 pt-16 border-t border-gray-800/50">
          {stats.map((stat, index) => (
            <div key={index} className="text-center space-y-2">
              <div className={`text-4xl md:text-5xl font-bold bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`}>
                {stat.value}
              </div>
              <div className="text-gray-400 font-medium">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;